import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import type {
  CommunicationAudienceFilter,
  CommunicationCampaignKind,
  CommunicationCampaignStatus,
} from '@mds/shared/types/communication';
import type { CustomerCategoryKey } from '@mds/shared/types/customer';

import { optionalText, requiredText } from '@/lib/validation';

/** How campaigns read, and the error codes a send can come back with. */

export const KIND_LABELS: Record<CommunicationCampaignKind, string> = {
  announcement: 'Announcement',
  invitation: 'Invitation',
};

export const categoryLabel = (key: CustomerCategoryKey) =>
  key.charAt(0).toUpperCase() + key.slice(1);

const STATUS: Record<CommunicationCampaignStatus, { label: string; cls: string }> = {
  queued: { label: 'Queued', cls: 'border-hairline bg-raised text-ink-secondary' },
  sending: { label: 'Sending', cls: 'border-warn-edge bg-warn-wash text-warn-text' },
  sent: { label: 'Sent', cls: 'border-brand-edge/35 bg-brand-wash text-brand-strong' },
  partially_failed: {
    label: 'Partly failed',
    cls: 'border-danger-edge bg-danger-wash text-danger-text',
  },
  failed: { label: 'Failed', cls: 'border-danger-edge bg-danger-wash text-danger-text' },
};

export const campaignStatus = (status: CommunicationCampaignStatus) => STATUS[status];

const oneLine = (v: string) => !/[\r\n]/.test(v);

/**
 * What a send would be refused for, checked before the confirmation opens:
 * the message, plus the audience's tags.
 * The audience itself may be empty — no filters means everyone who agreed to
 * email — so only its tags have rules of their own. The recipient count is the
 * server's answer, not something typed, and is checked where it is shown.
 */
export const composeSchema = z.object({
  subject: requiredText(180).refine(oneLine, 'Keep the subject on one line.'),
  preheader: optionalText(255).refine(oneLine, 'Keep the preview line on one line.'),
  body: requiredText(20_000),
  tags: z
    .array(
      z
        .string()
        .trim()
        .min(1, 'Remove the empty tag.')
        .max(64, 'Keep each tag to 64 characters or fewer.'),
    )
    .max(50, 'Up to 50 tags.')
    .refine(
      (list) => new Set(list.map((t) => t.toLowerCase())).size === list.length,
      'Each tag should appear once.',
    ),
});

/** Sent in a stable shape, so the same filters always compare equal. */
export const normaliseAudience = (a: CommunicationAudienceFilter): CommunicationAudienceFilter => ({
  ...(a.categoryKeys?.length ? { categoryKeys: [...a.categoryKeys].sort() } : {}),
  ...(a.tags?.length ? { tags: [...a.tags].sort() } : {}),
  ...(a.attendedEventIds?.length ? { attendedEventIds: [...a.attendedEventIds].sort() } : {}),
});

export const isEmptyAudience = (a: CommunicationAudienceFilter) =>
  !a.categoryKeys?.length && !a.tags?.length && !a.attendedEventIds?.length;

const errorBody = (error: unknown) =>
  error instanceof ApiError
    ? (error.body as { code?: string; details?: { eligibleCount?: number } } | null)
    : null;

/** The audience moved between preview and send; nothing was queued. */
export const audienceChangedTo = (error: unknown): number | null =>
  errorBody(error)?.code === 'COMMUNICATION_AUDIENCE_CHANGED'
    ? (errorBody(error)?.details?.eligibleCount ?? null)
    : null;

export const errorCode = (error: unknown) => errorBody(error)?.code ?? null;
